<template>
	<!-- Horizontal scroll wrapper -->
	<div class="data-table-wrapper" ref="wrapper">
		<!-- Scroll signifier -->
		<div v-show="isTableWider && !isScrollSignifierHidden" class="scroll-signifier">
			<div class="arrow" @click="scrollToEnd">
				<svg
					class="material-icons"
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
				>
					<path d="M10 17l5-5-5-5v10z" />
					<path fill="none" d="M0 24V0h24v24H0z" />
				</svg>
			</div>
		</div>

		<!-- Table -->
		<table class="table" ref="table">
			<!-- Header -->
			<thead>
				<tr>
					<th
						v-for="(item, i) in header"
						:key="i"
						@click="onSort(item)"
						:class="[
							{ active: item.isSorting && sortable },
							{ sorting: sortable },
							{ desc: item.direction === 'desc' }
						]"
					>
						<div class="headline">
							{{ item.text }}
							{{ item.label }}
						</div>

						<!-- Sort icons -->
						<template v-if="sortable && item.sortable !== false">
							<svg
								class="material-icons"
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
							>
								<path d="M7 14l5-5 5 5z" />
								<path d="M0 0h24v24H0z" fill="none" />
							</svg>
						</template>
					</th>
				</tr>
			</thead>

			<!-- Body -->
			<tbody :class="{ invisible: isLoading }">
				<!-- Rows -->
				<tr v-for="(item, i) in list" :key="i">
					<slot name="list" :item="item"></slot>
				</tr>
			</tbody>
		</table>

		<!-- Loading spinner -->
		<div v-if="isLoading" class="spinner-wrapper">
			<div class="spinner"></div>
		</div>

		<!--Empty state message -->
		<div class="empty" v-if="list.length === 0 && isLoading === false">
			<slot name="empty"></slot>
		</div>
	</div>
</template>

<script>
import throttle from "lodash.throttle"
import CrmIcon from "@/components/CrmIcon/"

export default {
	name: "j-data-table",
	data() {
		return {
			// IF table is wider than wrapper, show special signifier
			isTableWider: false,
			isScrollSignifierHidden: false
		}
	},

	props: {
		isLoading: {
			type: Boolean,
			default: false
		},
		header: {
			type: Array,
			required: false,
			deafult: () => []
		},
		list: {
			type: Array,
			required: true,
			default: () => []
		},
		// If header should have sort handles
		sortable: {
			type: Boolean,
			default: false
		}
	},

	methods: {
		// When user clicks on header item, change sorting
		// and propagate to parent via event
		onSort(clickedItem) {
			if (this.header.length === 0) return
			if (this.sortable !== true) return
			if (clickedItem.sortable === false) return

			let newHeader = this.header.map(item => {
				if (item === clickedItem)
					return {
						...item,
						isSorting: true,
						direction: item.direction === "desc" ? "asc" : "desc"
					}
				else return { ...item, isSorting: false }
			})

			this.$emit("sort", newHeader)
		},

		scrollToEnd() {
			this.$refs.wrapper.scrollLeft = this.$refs.wrapper.clientWidth + 100
		},

		onScroll(e) {
			if (e.target.scrollLeft > 5) this.isScrollSignifierHidden = true
			else this.isScrollSignifierHidden = false
		}
	},

	// Check size of table vs wrapper
	// If wrapper is wider, add signifier that it's possible to scroll table
	mounted() {
		if (this.$refs.wrapper.clientWidth - this.$refs.table.clientWidth < -80) {
			this.isTableWider = true
			this.$refs.wrapper.addEventListener(
				"scroll",
				throttle(e => {
					this.onScroll(e)
				}, 100)
			)
		}
	}
}
</script>

<style scoped>
@import "../../assets/css/tokens.css";

/* Hide scrollbars for Chrome */
.data-table-wrapper::-webkit-scrollbar {
	display: none !important;
}

.data-table-wrapper {
	position: relative;
	width: 100%;
	min-height: 150px;
	overflow: scroll;
	overflow-x: scroll;
	-ms-overflow-style: none; /* Hide scrollbars on ie11 */
	scrollbar-width: none; /* Hide scrollbars on firefox */
}

.data-table-wrapper.with-arrows {
	padding: 0 var(--spacing10);
}

.table {
	width: 100%;
	border-collapse: collapse;
}

thead > tr > th {
	position: relative;
	height: 48px;
	text-align: left;
	min-width: 150px;
	white-space: nowrap;
	padding-right: 16px;
	border-bottom: 1px solid var(--color7);
}

thead > tr > th > .headline {
	height: 100%;
	display: -webkit-inline-box;
	display: -moz-inline-box;
	display: -ms-inline-flexbox;
	display: -webkit-inline-flex;
	display: inline-flex;
	justify-content: flex-start;
	align-items: center;
	padding: 0 var(--spacing2);
	font-weight: var(--fontWeight7);
	font-size: var(--fontSize3);
	text-align: left;
	color: var(--color6);
}

thead > tr > th.sorting {
	cursor: pointer;
}

thead > tr > th.sorting:hover {
	background-color: var(--color4);
}

thead > tr > th.active .headline {
	color: var(--color1);
}

thead > tr > th > .material-icons {
	position: absolute;
	top: 0;
	justify-content: center;
	align-items: center;
	width: 18px;
	height: 100%;
	transition: all 160ms ease-out;
}

thead > tr > th.desc > .material-icons {
	transform: rotate(180deg);
}

thead > tr > th:first-child {
	padding-left: var(--spacing2);
}

thead > tr > th:last-child {
	text-align: right;
	padding-right: var(--spacing2);
}

thead > tr > th.sorting:last-child {
	padding-right: var(--spacing6);
}

tbody > tr {
	border-bottom: 1px solid var(--color7);
}

tbody > tr:last-child {
	border-bottom: 0;
}

tbody > tr > td {
	height: 48px;
	font-size: var(--fontSize3);
	line-height: var(--lineHeight125);
	white-space: nowrap;
	padding: 0 var(--spacing10) 0 var(--spacing2);
}

tbody tr:hover {
	background-color: var(--color4);
}

tbody > tr > td:first-child {
	padding-left: var(--spacing4);
}

tbody > tr > td:last-child {
	text-align: right;
	padding-right: var(--spacing4);
}

tbody > tr > td > a {
	display: flex;
	justify-content: flex-start;
	align-items: center;
	height: 100%;
	text-decoration: none;
	color: var(--color2);
}

tbody > tr > td > .action {
	display: inline-flex;
	justify-content: center;
	align-items: center;
	height: 100%;
	width: 32px;
	cursor: pointer;
}

tbody > tr > td > .action:hover {
	opacity: 0.7;
}

/* Modifier for td - paragraph */
tbody > tr > td.paragraph {
	white-space: normal;
	padding-top: var(--spacing2);
	padding-bottom: var(--spacing2);
	line-height: var(--lineHeight125);
}

tbody.invisible tr {
	opacity: 0;
	pointer-events: none;
	border-color: transparent;
}

/* Empty message */
.data-table-wrapper >>> .empty {
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	padding: var(--spacing10) 0;
	font-size: var(--fontSize3);
	color: var(--color6);
}

/* When wrapper is wider than table, show scroll signifier */
.scroll-signifier {
	position: absolute;
	top: 0;
	right: 0;
	height: 100%;
	width: 16px;
	background: linear-gradient(to right, var(--color5-alpha-30), var(--color5));
	pointer-events: none;
	z-index: 1;
}

.scroll-signifier > .arrow {
	position: absolute;
	top: 0;
	right: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 16px;
	height: 100%;
	background-color: var(--color5);
	box-shadow: -2px 0 16px -1px var(--color6-alpha-10);
	border-left: 1px solid var(--color7);
	color: var(--color1);
	cursor: pointer;
	pointer-events: all;
}

/* Loading spinner */
.data-table-wrapper >>> .spinner-wrapper {
	position: absolute;
	top: 0;
	display: flex;
	justify-content: center;
	align-items: flex-start;
	width: 100%;
	height: 100%;
	padding-top: var(--spacing15);
}

.spinner {
	width: 40px;
	height: 40px;
	background-color: var(--color2);
	border-radius: 100%;
	animation: sk-scaleout 1s infinite ease-in-out;
}

@keyframes sk-scaleout {
	0% {
		transform: scale(0);
	}
	100% {
		transform: scale(1);
		opacity: 0;
	}
}
</style>
